import { useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';

// Reveal when the card crosses 85% of the viewport height, matching the
// "top 85%" start the previous ScrollTrigger implementation used.
const REVEAL_ROOT_MARGIN = '0px 0px -15% 0px';

/**
 * Scroll-in reveal for cards.
 *
 * Uses IntersectionObserver rather than ScrollTrigger: the browser evaluates
 * intersection against live geometry, so the reveal cannot get stuck when a
 * pinned section or a late layout pass (image loads, computed grid heights)
 * moves the card after the animation was set up.
 */
export const ListingCardAnimate = ({ children, options = {} }) => {
  const itemRef = useRef(null);
  const {
    index = 0,
    y = 32,
    opacity = 0,
    duration = 0.6,
    repeat = false,
  } = options;

  // Layout effect so the card is hidden before the first paint rather than
  // flashing at full opacity for a frame.
  useLayoutEffect(() => {
    const el = itemRef.current;
    if (!el) return undefined;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      gsap.set(el, { opacity: 1, y: 0 });
      return undefined;
    }

    let tween = null;

    const reveal = isInitialViewport => {
      tween?.kill();

      if (isInitialViewport) {
        tween = gsap.fromTo(
          el,
          { opacity: 0.1, y: 15 },
          {
            opacity: 1,
            y: 0,
            duration: 0.4,
            ease: 'power2.out',
            delay: Math.min(index * 0.08, 0.3),
            overwrite: 'auto',
          }
        );
        return;
      }

      tween = gsap.to(el, {
        opacity: 1,
        y: 0,
        duration,
        ease: 'power3.out',
        delay: Math.min(index * 0.1, 0.4),
        overwrite: 'auto',
      });
    };

    const reset = () => {
      tween?.kill();
      tween = null;
      gsap.set(el, { opacity, y });
    };

    gsap.set(el, { opacity, y });

    if (typeof IntersectionObserver === 'undefined') {
      reveal(true);
      return () => tween?.kill();
    }

    // The card is never measured at mount: images and pinned sections above it
    // may not have taken up their space yet, so a card that is really below the
    // fold would measure as visible and reveal itself before it is ever seen.
    // The observer keeps re-evaluating while the layout settles.
    let isFirstCallback = true;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Cards on screen from the start get the shorter intro so an
            // above-the-fold grid doesn't crawl in.
            reveal(isFirstCallback);
            if (!repeat) observer.disconnect();
          } else if (!isFirstCallback && repeat) {
            reset();
          }
          isFirstCallback = false;
        });
      },
      { rootMargin: REVEAL_ROOT_MARGIN, threshold: 0 }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
      tween?.kill();
    };
  }, [index, y, opacity, duration, repeat]);

  return <div ref={itemRef}>{children}</div>;
};
